const PRE_QUIZ_STORAGE_KEY = 'pre_quiz_wu_7674_data';
const QUIZ_STORAGE_KEY = 'quiz_data';
const KLAVIYO_SUBSCRIPTION_URL = 'https://a.klaviyo.com/client/subscriptions?company_id=SQEMbF';
const RETURNING_CUSTOMER_CHECK_URL =
  'https://n8e6q2uda0.execute-api.us-east-1.amazonaws.com/prod/check-if-customer-is-returning';
const KLAVIYO_ASANA_OID_KEY = 'asana-oid';
const QUIZ_DEFAULT_NAME = 'quiz_v2';
const QUIZ_STEP_PRE_FINAL = 'pre-final';
// A step that auto-advances the user onward without a NEXT click (a loading screen that
// auto-continues, or a JS redirect to the offer). Such a step must NOT become a browser
// Back target, so the real destination stays reachable via the Back button.
const TRANSIENT_STEP_SELECTOR = 'quiz-loader, quiz-step-change, quiz-step-redirect';
// Published via Shopify.analytics.publish (Shopify custom events) and forwarded to Mixpanel —
// these do NOT go through window.ElevarDataLayer like the rest of our Mixpanel events.
const EVENT_QUIZ_STARTED = 'quiz_started';
const EVENT_QUIZ_STEP_VIEWED = 'quiz_step_viewed'; // published on step ENTRY (screen shown)
const EVENT_QUIZ_STEP_COMPLETED = 'quiz_step_completed'; // published on step EXIT (screen left)
const EVENT_QUIZ_COMPLETED = 'quiz_completed';
const EVENT_QUIZ_STEP_ANSWERED = 'quiz_step_answered';
const KLAVIYO_JOINT_QUIZ_COMPLETED_KEY = 'joint-health-quiz_completed';
const QUIZ_KLAVIYO_PAYLOAD_KEY = 'quiz_klaviyo_payload';

const WEIGHT_RANGES = [
  { max: 35, label: '0-35lbs' },
  { max: 65, label: '36-65lbs' },
  { max: 100, label: '65-100lbs' },
  { max: 120, label: '100-120lbs' },
  { max: Infinity, label: 'Over120lbs' },
];
const PLACEHOLDER_HIDE_VALUE = 'unknown';

function deriveAgeWord(ageStr) {
  return parseInt(ageStr, 10) === 1 ? 'year' : 'years';
}

if (!customElements.get('main-quiz')) {
  class MainQuiz extends HTMLElement {
    constructor() {
      super();
      this.variablesToSendToTargetPage = [];
      this.correctAnswers = 0;
      this.totalQuestions = 0;
      this.savedPrevStepNames = [];
      this.recommendationResult = null;
      this.recommendationConfig = {};
      this.completedSteps = new Set();
      this.viewedSteps = new Set();
      this.answeredSteps = [];
    }

    get redirectElement() {
      return this.querySelector('quiz-step-redirect');
    }

    connectedCallback() {
      this.recommendationConfig = JSON.parse(this.dataset.recommendationConfig || '{}');
      this.storageKey = this.dataset.storageKey || QUIZ_STORAGE_KEY;
      this.klaviyoPayloadKey = this.dataset.klaviyoPayloadKey || QUIZ_KLAVIYO_PAYLOAD_KEY;
      this.answeredStepsKey = `${this.storageKey}_answered_steps`;
      this.answeredSteps = JSON.parse(localStorage.getItem(this.answeredStepsKey)) || [];
      this.hasEmailStep = !!this.querySelector('input[type="email"][data-quiz-input]');
      this.setupEventListeners();
      if (!Shopify.designMode) this.setupPlaceholders();
      this.includeScoreInRedirect = this.redirectElement?.dataset.includeScoreParams === 'true';
      this.excludeEmailFromRedirectUrl = this.redirectElement?.dataset.excludeEmailFromRedirectUrl === 'true';
      if (!Shopify.designMode) {
        const quizName = this.dataset.quizName || QUIZ_DEFAULT_NAME;
        Shopify.analytics.publish(EVENT_QUIZ_STARTED, { quiz_name: quizName });
      }
      this.setupInitialStep();
      if (!Shopify.designMode) this.initPlaceholderValuesFromLocalStorage();
      if (!Shopify.designMode) this.initInputValuesFromLocalStorage();
    }

    calculateScoreFromStoredAnswers() {
      const answers = JSON.parse(localStorage.getItem(this.storageKey)) || {};
      this.totalQuestions = Object.keys(answers).length;
      this.correctAnswers = Object.values(answers).filter((result) => result === 'correct').length;
    }

    calcAgeFromBirthday(year, monthName) {
      const monthNum =
        {
          January: 1,
          February: 2,
          March: 3,
          April: 4,
          May: 5,
          June: 6,
          July: 7,
          August: 8,
          September: 9,
          October: 10,
          November: 11,
          December: 12,
        }[monthName] || parseInt(monthName, 10);
      if (!year || !monthNum) return null;
      const birth = new Date(parseInt(year, 10), monthNum - 1, 1);
      return Math.floor((new Date() - birth) / (365.25 * 24 * 60 * 60 * 1000));
    }

    buildTrackPayload(data) {
      const props = {};
      const unknownBreedValues = ["don't know", 'Not sure', 'Not listed'];
      const unknownAgeValue = "don't know";

      const skipKeys = new Set(['customer-email', 'age-know', 'birthday-year', 'birthday-month', 'format-preference']);
      Object.entries(data).forEach(([key, value]) => {
        if (skipKeys.has(key)) return;
        props[`dog1_${key}`] = value;
      });

      if (data['weight']) {
        const weightInt = parseInt(data['weight'], 10);
        props.dog1_weight_int = weightInt;
        props.dog1_weight = WEIGHT_RANGES.find((r) => weightInt <= r.max).label;
      }

      if (data['issues']) {
        props.dog1_issues_list = data['issues']
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }

      if (data['food-allergens']) {
        props.dog1_dietary_restrictions = data['food-allergens'];
        props.dog1_dietary_restrictions_list = data['food-allergens']
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }
      delete props['dog1_food-allergens'];

      const ageKnown = data['age-know'] !== 'no';

      if (ageKnown) {
        const birthdayAge = this.calcAgeFromBirthday(data['birthday-year'], data['birthday-month']);
        if (birthdayAge !== null) {
          props.dog1_age = birthdayAge;
        } else if (data['age'] && !data['age'].includes(unknownAgeValue)) {
          props.dog1_age = parseInt(data['age'], 10) || 'unknown';
        } else {
          props.dog1_age = 'unknown';
        }

        if (data['birthday-year'] || data['birthday-month']) {
          const monthNum =
            {
              January: '01',
              February: '02',
              March: '03',
              April: '04',
              May: '05',
              June: '06',
              July: '07',
              August: '08',
              September: '09',
              October: '10',
              November: '11',
              December: '12',
            }[data['birthday-month']] || (data['birthday-month'] || '').padStart(2, '0');
          props.dog1_birthday = `${data['birthday-year'] || ''}-${monthNum}-01`;
        }
      } else {
        props.dog1_age =
          data['age'] && !data['age'].includes(unknownAgeValue) ? parseInt(data['age'], 10) || 'unknown' : 'unknown';
      }

      if (data['breed'] && unknownBreedValues.some((v) => data['breed'].includes(v))) {
        props.dog1_breed = 'unknown';
      }

      props.dog1_quiz_completed = this.dataset.quizType || '';
      props.dog1_result_URL = this.redirectUrl || null;

      const asanaOid = this.dataset.klaviyoAsanaOid;
      if (asanaOid) props[KLAVIYO_ASANA_OID_KEY] = asanaOid;

      if (this.recommendationResult?.klaviyoProps) {
        Object.assign(props, this.recommendationResult.klaviyoProps);
      }

      Object.keys(props).forEach((k) => {
        if (props[k] === null) delete props[k];
      });

      return props;
    }

    async sendDataToKlaviyo(data) {
      const klaviyoSource = this.dataset.klaviyoSource || '';
      const eventName = this.dataset.klaviyoEventName;

      if (typeof klaviyo !== 'undefined') {
        klaviyo.push(['identify', { $email: data.email || data['customer-email'] }]);
        if (eventName) {
          klaviyo.push(['track', eventName, this.buildTrackPayload(data)]);
        }
      }

      if (!this.dataset.klaviyoListId) return;

      const klaviyoSubscriptionPayload = {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: data.email || data['customer-email'],
                properties: {
                  $source: klaviyoSource,
                  source_handle: klaviyoSource,
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.dataset.klaviyoListId,
            },
          },
        },
      };
      Object.assign(
        klaviyoSubscriptionPayload.attributes.profile.data.attributes.properties,
        this.buildTrackPayload(data),
      );
      if (this.excludeEmailFromRedirectUrl) {
        klaviyoSubscriptionPayload.attributes.profile.data.attributes.properties[KLAVIYO_JOINT_QUIZ_COMPLETED_KEY] =
          true;
      }
      const KLoptions = {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({ data: klaviyoSubscriptionPayload }),
      };

      await fetch(KLAVIYO_SUBSCRIPTION_URL, KLoptions);
    }

    initInputValuesFromLocalStorage() {
      const quizData = JSON.parse(localStorage.getItem(this.storageKey)) || {};
      const inputGroups = this.querySelectorAll('input-group');
      inputGroups.forEach((inputGroup) => {
        const key = inputGroup.input.name;
        if (quizData[key]) {
          inputGroup.changeInputValue(quizData[key]);
        }
      });

      Object.entries(quizData).forEach(([key, value]) => {
        const savedValues = value.split(',').map((v) => v.trim());
        this.querySelectorAll(`input[type="checkbox"][data-quiz-input][name="${key}"]`).forEach((cb) => {
          const shouldBeChecked = savedValues.includes(cb.value);
          if (cb.checked !== shouldBeChecked) {
            cb.checked = shouldBeChecked;
            cb.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      Object.entries(quizData).forEach(([key, value]) => {
        const radio = this.querySelector(`input[type="radio"][data-quiz-input][name="${key}"][value="${value}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      customElements.whenDefined('quiz-step').then(() => {
        this.querySelectorAll('quiz-step').forEach((step) => {
          step.syncExclusiveStates();
          step.updateNextButtonState();
        });
      });
    }

    initPlaceholderValuesFromLocalStorage() {
      const quizData = JSON.parse(localStorage.getItem(this.storageKey)) || {};

      const aliasMap = {};
      this.querySelectorAll('[data-placeholder-key]').forEach((input) => {
        aliasMap[input.dataset.placeholderKey] = quizData[input.name] || quizData[input.dataset.placeholderKey];
      });

      const resolveKey = (key) => (aliasMap[key] !== undefined ? aliasMap[key] : quizData[key]);

      this.querySelectorAll('[data-placeholder-name]').forEach((placeholder) => {
        const value = resolveKey(placeholder.dataset.placeholderName) || '';
        this.applyPlaceholderValue(placeholder, value);
      });

      this.querySelectorAll('[data-placeholder-template]').forEach((input) => {
        input.placeholder = input.dataset.placeholderTemplate.replace(/\[\[([^\]]+)\]\]/g, (_match, key) => {
          return resolveKey(key) || '';
        });
      });

      const ageValue = resolveKey('age');
      if (ageValue && ageValue !== 'unknown') {
        this.querySelectorAll('[data-placeholder-name="age_word"]').forEach((placeholder) => {
          this.applyPlaceholderValue(placeholder, deriveAgeWord(ageValue));
        });
      }
    }

    setupInitialStep() {
      const urlParams = new URLSearchParams(window.location.search);
      const stepName = urlParams.get('quiz-step');
      if (stepName) {
        const stepToActivate = this.querySelector(`[data-step-name="${stepName}"]`);
        if (stepToActivate) {
          this.querySelectorAll('quiz-step').forEach((step) => {
            step.style.display = 'none';
          });
          stepToActivate.style.display = '';
          this.publishStepViewed(stepToActivate);
        }
        return;
      }
      // No ?quiz-step param: the default visible first step is the entry screen.
      const defaultStep =
        this.querySelector('quiz-step:not([style*="display: none"])') || this.querySelector('quiz-step');
      this.publishStepViewed(defaultStep);
    }

    setupPlaceholders() {
      const nodeList = [];
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => (/\[\[.*?\]\]/.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
      });

      while (walker.nextNode()) {
        nodeList.push(walker.currentNode);
      }

      nodeList.forEach((node) => {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        node.nodeValue.replace(/\[\[(.*?)\]\]/g, (match, key, offset) => {
          if (offset > lastIndex) {
            fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex, offset)));
          }

          const span = document.createElement('span');
          span.dataset.placeholderName = key;
          fragment.appendChild(span);

          lastIndex = offset + match.length;
        });

        if (lastIndex < node.nodeValue.length) {
          fragment.appendChild(document.createTextNode(node.nodeValue.slice(lastIndex)));
        }

        node.parentNode.replaceChild(fragment, node);
      });

      this.querySelectorAll('[data-placeholder-template]').forEach((input) => {
        input.dataset.placeholderResolved = input.dataset.placeholderTemplate;
      });
    }

    setupEventListeners() {
      if (Shopify.designMode) {
        this.onBlockSelect = (event) => {
          const targetInsideQuiz = event.target.closest('main-quiz');
          if (!targetInsideQuiz) return;

          const quizStep = event.target.closest('quiz-step') || event.target.querySelector('quiz-step');
          const currentStep = this.querySelector('quiz-step:not([style*="display: none"])');
          if (!quizStep || !currentStep) return;

          this.changeStep(quizStep.dataset.stepName, currentStep);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      // bfcache restore: browser back from the result page skips connectedCallback
      // so we re-run setupInitialStep when the page is restored from bfcache
      this.onPageShow = (event) => {
        if (event.persisted) this.setupInitialStep();
      };
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
      window.removeEventListener('pageshow', this.onPageShow);
    }

    pushStepAnsweredEvent(stepEl, answers) {
      if (Shopify.designMode) return;
      const analyticsName = stepEl.dataset.analyticsName;
      if (!analyticsName) return;
      const quizName = this.dataset.quizName || QUIZ_DEFAULT_NAME;
      const questionTitle = stepEl.dataset.questionTitle || analyticsName;
      const answer = Object.values(answers).filter(Boolean).join(', ');
      const existingIndex = this.answeredSteps.findIndex((s) => s.title === questionTitle);
      if (existingIndex !== -1) {
        this.answeredSteps[existingIndex].reply = answer;
      } else {
        this.answeredSteps.push({ title: questionTitle, reply: answer });
      }
      localStorage.setItem(this.answeredStepsKey, JSON.stringify(this.answeredSteps));
      Shopify.analytics.publish(EVENT_QUIZ_STEP_ANSWERED, {
        quiz_name: quizName,
        question_title: questionTitle,
        answer,
      });
    }

    publishStepViewed(stepEl) {
      if (Shopify.designMode || !stepEl) return;
      const analyticsName = stepEl.dataset.analyticsName;
      if (!analyticsName || this.viewedSteps.has(analyticsName)) return;
      this.viewedSteps.add(analyticsName);
      const quizName = this.dataset.quizName || QUIZ_DEFAULT_NAME;
      const questionTitle = stepEl.dataset.questionTitle || analyticsName;
      Shopify.analytics.publish(EVENT_QUIZ_STEP_VIEWED, { quiz_name: quizName, question_title: questionTitle });
      if (analyticsName === 'plan_ready') {
        Shopify.analytics.publish(EVENT_QUIZ_COMPLETED, this.buildCompletedPayload());
      }
    }

    buildCompletedPayload() {
      const quizName = this.dataset.quizName || QUIZ_DEFAULT_NAME;
      const payload = { event: EVENT_QUIZ_COMPLETED, quiz_name: quizName };
      this.answeredSteps.forEach(({ title, reply }, i) => {
        payload[`q${i + 1}_title`] = title;
        payload[`q${i + 1}_reply`] = reply;
      });
      return payload;
    }

    applyPlaceholderValue(span, value) {
      const displayValue = value;
      const shouldHide = !displayValue || displayValue === PLACEHOLDER_HIDE_VALUE;
      const p = span.closest('p') || span.parentElement;
      const innerBlock = p.closest('.block__richtext') || p.closest('.block__stack');
      const badgeBlock = innerBlock?.parentElement?.closest('.block__stack') || innerBlock || p;
      if (shouldHide) {
        span.textContent = '';
        badgeBlock.classList.add('hidden');
      } else {
        span.textContent = displayValue;
        badgeBlock.classList.remove('hidden');
      }
    }

    updatePlaceholders(key, value) {
      const placeholderKeyEl = this.querySelector(`[data-placeholder-key][name="${key}"]`);
      const aliasKey = placeholderKeyEl ? placeholderKeyEl.dataset.placeholderKey : null;

      this.querySelectorAll(`[data-placeholder-name="${key}"]`).forEach((p) => {
        this.applyPlaceholderValue(p, value);
      });
      if (aliasKey) {
        this.querySelectorAll(`[data-placeholder-name="${aliasKey}"]`).forEach((p) => {
          this.applyPlaceholderValue(p, value);
        });
      }

      if (key === 'age') {
        this.querySelectorAll('[data-placeholder-name="age_word"]').forEach((p) => {
          this.applyPlaceholderValue(p, deriveAgeWord(value));
        });
      }

      this.querySelectorAll('[data-placeholder-template]').forEach((input) => {
        const template = input.dataset.placeholderResolved || input.dataset.placeholderTemplate;
        const quizData = JSON.parse(localStorage.getItem(this.storageKey)) || {};
        const aliasMapLocal = {};
        this.querySelectorAll('[data-placeholder-key]').forEach((el) => {
          aliasMapLocal[el.dataset.placeholderKey] = quizData[el.name] || quizData[el.dataset.placeholderKey];
        });
        if (aliasKey) aliasMapLocal[aliasKey] = value;
        aliasMapLocal[key] = value;
        input.placeholder = template.replace(/\[\[([^\]]+)\]\]/g, (_match, k) => {
          return aliasMapLocal[k] !== undefined ? aliasMapLocal[k] : quizData[k] || '';
        });
      });
    }

    updateLocalStorageQuizInput(key, value) {
      const quizData = JSON.parse(localStorage.getItem(this.storageKey)) || {};
      quizData[key] = value.trim();
      localStorage.setItem(this.storageKey, JSON.stringify(quizData));
      return quizData;
    }

    updateVariablesToSendToTargetPage(input) {
      if (input.dataset.sendToTargetPage === 'true' && !this.variablesToSendToTargetPage.includes(input.name)) {
        this.variablesToSendToTargetPage.push(input.name);
      }
    }

    changeStep(changeToStepName, currentStepElement, saveToHistory = true) {
      const stepToActivate = this.querySelector(`[data-step-name="${changeToStepName}"]`);
      if (stepToActivate) {
        currentStepElement.deactivate();
        if (!Shopify.designMode) {
          const analyticsName = currentStepElement.dataset.analyticsName;
          if (analyticsName && !this.completedSteps.has(analyticsName)) {
            this.completedSteps.add(analyticsName);
            const quizName = this.dataset.quizName || QUIZ_DEFAULT_NAME;
            const questionTitle = currentStepElement.dataset.questionTitle || analyticsName;
            Shopify.analytics.publish(EVENT_QUIZ_STEP_COMPLETED, {
              quiz_name: quizName,
              question_title: questionTitle,
            });
          }
        }
        if (saveToHistory) this.savedPrevStepNames.push(currentStepElement.dataset.stepName);
        stepToActivate.activate();
        this.publishStepViewed(stepToActivate);
        this.updateURLParamAndSetHistory('quiz-step', changeToStepName, stepToActivate);
      }
    }

    updateURLParamAndSetHistory(key, value, stepElement) {
      // A transient step auto-advances or redirects the user onward (a loader, or a JS
      // redirect-to-offer screen). It must leave the URL/history untouched so the Back button
      // returns to the last step the user actually stopped on — not to this fly-through screen,
      // which would just re-run its redirect. (quiz-v2: the redirect screen; jcqf: the loader.)
      if (stepElement?.querySelector(TRANSIENT_STEP_SELECTOR)) return;

      const url = new URL(window.location);
      url.searchParams.set(key, value);
      // Push a real history entry — so Back from the offer page lands here — for steps the user
      // stops on before leaving: "pre-final" (quiz-v2's email screen) and a final landing step
      // at the end of the chain (jcqf's results screen). Other steps replace the entry.
      const isStopStep = value === QUIZ_STEP_PRE_FINAL || !stepElement?.dataset.nextStepName;
      if (isStopStep) {
        window.history.pushState({}, '', url);
      } else {
        window.history.replaceState({}, '', url);
      }
    }
  }

  customElements.define('main-quiz', MainQuiz);
}

if (!customElements.get('quiz-step')) {
  class QuizStep extends HTMLElement {
    get quiz() {
      return this.closest('main-quiz');
    }

    constructor() {
      super();
    }

    get inputs() {
      return this.querySelectorAll('[data-quiz-input]');
    }

    connectedCallback() {
      this.backButton = this.querySelector('[data-back-button]');
      this.buttons = this.querySelectorAll('[data-quiz-input-button]');
      this.nextButton = this.querySelector('[data-next-button]');
      if (this.nextButton) {
        this.boundNextButtonClick = this.onNextButtonClick.bind(this);
        this.nextButton.addEventListener('click', this.boundNextButtonClick);
      }
      this.setupEventListeners();
      this.setupVariablesToSendToTargetPage();
      this.updateNextButtonState();
    }

    disconnectedCallback() {
      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.boundNextButtonClick);
      }
      if (this.backButton && this.onBackButtonClick) {
        this.backButton.removeEventListener('click', this.onBackButtonClick);
      }
      if (this.buttonClickHandlers) {
        this.buttonClickHandlers.forEach(({ button, handler }) => {
          button.removeEventListener('click', handler);
        });
      }
      if (this.onChange) {
        this.removeEventListener('change', this.onChange);
      }
      if (this.onInput) {
        this.removeEventListener('input', this.onInput);
      }
    }

    deactivate() {
      this.style.display = 'none';
    }

    activate() {
      this.style.display = '';
      this.dispatchEvent(new CustomEvent('quiz:stepActivated', { bubbles: true }));
      this.redirectElement = this.querySelector('quiz-step-redirect');
      if (this.redirectElement && !Shopify.designMode) this.redirect();

      this.stepChangeElement = this.querySelector('quiz-step-change');
      if (this.stepChangeElement && !Shopify.designMode) this.changeStepWithTimeout();

      const validationEls = this.querySelectorAll('[data-quiz-validation-error]');
      if (validationEls.length) {
        const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
        validationEls.forEach((el) => {
          el.style.display = 'none';
          delete quizData[el.dataset.groupName];
        });
        localStorage.setItem(this.quiz.storageKey, JSON.stringify(quizData));
      }

      this.updateNextButtonState();
    }

    isGroupValid(groupName) {
      if (this.querySelector(`input[type="checkbox"][name="${groupName}"]:checked`)) return true;
      if (this.querySelector(`input[type="radio"][name="${groupName}"]:checked`)) return true;
      const textInput = this.querySelector(`input:not([type="checkbox"]):not([type="radio"])[name="${groupName}"]`);
      if (textInput) {
        if (textInput.dataset.autocomplete !== undefined) return this.autocompleteValueValid(textInput);
        if (textInput.value.trim() === '') return false;
        // For email inputs use a strict regex: requires domain.tld (e.g. liza@gmail.com)
        if (textInput.type === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(textInput.value.trim());
        return true;
      }
      const select = this.querySelector(`select[name="${groupName}"]`);
      if (select) return select.value !== '';
      return false;
    }

    resolveNextStep() {
      const group = this.dataset.conditionalGroup;
      const matchStep = this.dataset.conditionalNext;
      const rawValues = this.dataset.conditionalValues;
      if (group && matchStep && rawValues) {
        const watchValues = rawValues.split(',').map((v) => v.trim());
        const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
        const stored = (quizData[group] || '').split(',').map((v) => v.trim());
        const hasMatch = stored.some((v) => watchValues.includes(v));
        if (hasMatch) return matchStep;
      }
      return this.dataset.nextStepName;
    }

    getStepInputGroupNames() {
      const names = new Set();
      this.querySelectorAll('[data-quiz-input][name], [data-quiz-select][name]').forEach((el) => {
        if (el.name) names.add(el.name);
      });
      return [...names];
    }

    updateNextButtonState() {
      if (!this.nextButton) return;

      const isBlocked = this.dataset.blockingValues !== undefined;
      const isRequired = this.dataset.required === 'true';

      const raw = this.dataset.requiredGroups;
      let groups = raw
        ? raw
            .split(',')
            .map((g) => g.trim())
            .filter(Boolean)
        : [];

      // When a step is marked required without explicit groups, validate every input group present in the step
      if (groups.length === 0 && isRequired) {
        groups = this.getStepInputGroupNames();
      }

      const groupsValid = groups.length === 0 || groups.every((g) => this.isGroupValid(g));

      this.nextButton.disabled = isBlocked || !groupsValid;
    }

    saveCheckboxGroupToLocalStorage(name) {
      const checked = [...this.querySelectorAll(`input[type="checkbox"][data-quiz-input][name="${name}"]:checked`)];
      if (checked.length) {
        this.quiz.updateLocalStorageQuizInput(name, checked.map((cb) => cb.value).join(','));
      } else {
        const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
        delete quizData[name];
        localStorage.setItem(this.quiz.storageKey, JSON.stringify(quizData));
      }
    }

    saveRadioToLocalStorage(name) {
      const checked = this.querySelector(`input[type="radio"][data-quiz-input][name="${name}"]:checked`);
      if (checked) {
        this.quiz.updateLocalStorageQuizInput(name, checked.value);
      } else {
        const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
        delete quizData[name];
        localStorage.setItem(this.quiz.storageKey, JSON.stringify(quizData));
      }
    }

    async customerIsReturning(email) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // abort after 3 s to prevent hanging requests on slow connections
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      };

      try {
        const res = await fetch(RETURNING_CUSTOMER_CHECK_URL, options);
        if (!res.ok) {
          return false;
        }

        const data = await res.json();
        clearTimeout(timeoutId);
        return data;
      } catch (error) {
        return false;
      }
    }

    async redirect() {
      const timeout = this.redirectElement.dataset.timeout * 1000;
      // poll until redirectUrl is set by the async returning-customer check (max 4 s)
      const waitForUrl = () =>
        new Promise((resolve) => {
          if (this.quiz.redirectUrl) return resolve();
          const interval = setInterval(() => {
            if (this.quiz.redirectUrl) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 4000);
        });

      await waitForUrl();
      // delay matches the timeout configured in block settings
      setTimeout(() => {
        if (this.quiz.redirectUrl) window.location.href = this.quiz.redirectUrl;
      }, timeout);
    }

    async changeStepWithTimeout() {
      if (!this.stepChangeElement) return;

      const timeout = parseInt(this.stepChangeElement.dataset.timeout) || 0;
      const nextStepName = this.stepChangeElement.dataset.nextStep;

      if (!nextStepName || timeout <= 0) return;

      // intentional delay: step auto-advances after block-configured timeout
      setTimeout(() => {
        this.quiz.changeStep(nextStepName, this, false);
      }, timeout * 1000);
    }

    getOfferUrl(quizData) {
      if (!this.quiz.redirectElement) return new URL(window.location.href);
      const url = new URL(
        `${window.location.protocol}//${window.location.host}${this.quiz.redirectElement.dataset.url}`,
      );

      Object.keys(quizData).forEach((key) => {
        if (!this.quiz.variablesToSendToTargetPage.includes(key)) return;
        url.searchParams.set('quiz_' + key, quizData[key]);
      });

      return url;
    }

    setupVariablesToSendToTargetPage() {
      this.inputs.forEach((input) => {
        this.quiz.updateVariablesToSendToTargetPage(input);
      });
      this.buttons.forEach((button) => {
        this.quiz.updateVariablesToSendToTargetPage(button);
      });
      this.querySelectorAll('[data-quiz-select]').forEach((select) => {
        this.quiz.updateVariablesToSendToTargetPage(select);
      });
    }

    onNextButtonClick() {
      let containsInvalidInput = false;
      const quizInputs = {};

      const selects = this.querySelectorAll('[data-quiz-select]');
      selects.forEach((select) => {
        if (select.value) {
          Object.assign(quizInputs, this.quiz.updateLocalStorageQuizInput(select.name, select.value));
          this.quiz.updateVariablesToSendToTargetPage(select);
        }
      });

      const checkboxGroups = {};
      this.querySelectorAll('input[type="checkbox"][data-quiz-input]:checked').forEach((cb) => {
        if (!checkboxGroups[cb.name]) checkboxGroups[cb.name] = [];
        checkboxGroups[cb.name].push(cb.value);
      });
      Object.entries(checkboxGroups).forEach(([name, values]) => {
        Object.assign(quizInputs, this.quiz.updateLocalStorageQuizInput(name, values.join(',')));
      });

      const radioGroups = {};
      this.querySelectorAll('input[type="radio"][data-quiz-input]:checked').forEach((radio) => {
        radioGroups[radio.name] = radio.value;
      });
      Object.entries(radioGroups).forEach(([name, value]) => {
        Object.assign(quizInputs, this.quiz.updateLocalStorageQuizInput(name, value));
      });

      for (const input of this.inputs) {
        if (input.type === 'checkbox' || input.type === 'radio') continue;
        // exclusive checkbox clears sibling text/number — don't let the empty value overwrite it
        if (!input.value.trim() && this.querySelector(`input[type="checkbox"][name="${input.name}"]:checked`)) continue;

        const hasExternalValidation = this.querySelector(
          `[data-quiz-validation-error][data-group-name="${input.name}"]`,
        );

        if (input.dataset.autocomplete !== undefined && !this.autocompleteValueValid(input)) {
          if (!hasExternalValidation) input.parentElement.setAutocompleteDefaultValue();
        }

        if (hasExternalValidation) continue;

        const inputValid = input.closest('input-group').validate();

        if (inputValid) {
          Object.assign(quizInputs, this.quiz.updateLocalStorageQuizInput(input.name, input.value));
          this.quiz.updatePlaceholders(input.name, input.value);

          if (input.type === 'email') {
            // Keeping this sync to avoid button lag before step change
            this.customerIsReturning(input.value).then((isReturning) => {
              const allQuizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
              this.setRedirectUrl(isReturning, allQuizData);
              if (this.quiz.hasEmailStep) {
                if (typeof klaviyo !== 'undefined') {
                  klaviyo.push(['identify', { $email: allQuizData['customer-email'] || '' }]);
                }
                localStorage.setItem(
                  this.quiz.klaviyoPayloadKey,
                  JSON.stringify({
                    email: allQuizData['customer-email'] || '',
                    properties: this.quiz.buildTrackPayload(allQuizData),
                  }),
                );
              }
              if (!Shopify.designMode && !this.quiz.querySelector('[data-analytics-name="plan_ready"]')) {
                Shopify.analytics.publish(EVENT_QUIZ_COMPLETED, this.quiz.buildCompletedPayload());
              }
            });
          }
        } else {
          containsInvalidInput = true;
        }
      }

      const validationErrorEls = [...this.querySelectorAll('[data-quiz-validation-error]')];
      if (validationErrorEls.length) {
        const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
        validationErrorEls.forEach((el) => {
          const groupName = el.dataset.groupName;
          el.style.display = 'none';

          const hasStoredValue = !!quizData[groupName];
          const checkedBox = this.querySelector(`input[type="checkbox"][name="${groupName}"]:checked`);
          const filledInput = this.querySelector(`input:not([type="checkbox"])[name="${groupName}"]`);
          const hasFilledInput = filledInput && filledInput.value.trim() !== '';
          const filledSelect = this.querySelector(`select[name="${groupName}"]`);
          const hasFilledSelect = filledSelect && filledSelect.value !== '';
          const autocompleteInput = this.querySelector(`input[data-autocomplete][name="${groupName}"]`);
          const hasFilledAutocomplete = autocompleteInput && autocompleteInput.value.trim() !== '';
          if (!hasStoredValue && !checkedBox && !hasFilledInput && !hasFilledSelect && !hasFilledAutocomplete) {
            el.textContent = el.dataset.errorMessage;
            el.style.display = '';
            containsInvalidInput = true;
          }
        });
      }

      if (containsInvalidInput) return;
      const stepInputNames = new Set([
        ...[...this.inputs]
          .map((i) => i.name)
          .filter((n) => {
            const input = this.querySelector(`input[name="${n}"]`);
            return !input || input.type !== 'email';
          }),
        ...[...this.querySelectorAll('[data-quiz-select]')].map((s) => s.name),
      ]);
      const answeredInputs = Object.fromEntries(Object.entries(quizInputs).filter(([k]) => stepInputNames.has(k)));
      if (Object.keys(answeredInputs).length) {
        this.quiz.pushStepAnsweredEvent(this, answeredInputs);
      } else if (this.dataset.quizComplete === 'true' && this.dataset.analyticsName) {
        this.quiz.pushStepAnsweredEvent(this, { [this.dataset.analyticsName]: 'confirmed' });
      }
      const hasEmailInput = !!this.querySelector('input[type="email"][data-quiz-input]');
      // For quizzes without a plan_ready step, publish quiz_completed on the final step click.
      // For quizzes with plan_ready, it is published in publishStepViewed instead.
      const hasPlanReadyStep = !!this.quiz.querySelector('[data-analytics-name="plan_ready"]');
      if (this.dataset.quizComplete === 'true' && !hasEmailInput && !Shopify.designMode && !hasPlanReadyStep) {
        Shopify.analytics.publish(EVENT_QUIZ_COMPLETED, this.quiz.buildCompletedPayload());
      }
      if (this.quiz.excludeEmailFromRedirectUrl) {
        this.setRedirectUrl(false, quizInputs);
      }

      this.quiz.changeStep(this.resolveNextStep(), this);
    }

    setRedirectUrl(isReturning, quizInputs) {
      const largeBreedWeights = ['65-100lbs', '100-120lbs', 'Over120lbs'];
      let baseUrl;
      if (isReturning && this.quiz.redirectElement) {
        let url = this.quiz.redirectElement.dataset.returningCustomerSbUrl;
        const weightInt = parseInt(quizInputs.weight, 10);
        const weightLabel = !isNaN(weightInt)
          ? WEIGHT_RANGES.find((r) => weightInt <= r.max)?.label
          : quizInputs.weight;
        if (largeBreedWeights.includes(weightLabel)) {
          url = this.quiz.redirectElement.dataset.returningCustomerLbUrl;
        }
        baseUrl = this.getOfferUrl(quizInputs);
        const returningUrl = new URL(url, window.location.origin);
        returningUrl.search = baseUrl.search;
        baseUrl = returningUrl;
      } else {
        baseUrl = this.getOfferUrl(quizInputs);
      }

      if (this.quiz.includeScoreInRedirect) {
        this.quiz.calculateScoreFromStoredAnswers();

        baseUrl.searchParams.set('correct', this.quiz.correctAnswers);
        baseUrl.searchParams.set('total', this.quiz.totalQuestions);
      }

      const stored = localStorage.getItem(PRE_QUIZ_STORAGE_KEY);

      if (stored) {
        const data = JSON.parse(stored);

        if (data.email && typeof klaviyo !== 'undefined') {
          klaviyo.push(['identify', { $email: data.email }]);
        }
      }

      // Allow quiz-issue-recommender (and any other listeners) to mutate baseUrl before redirect is set
      this.quiz.dispatchEvent(
        new CustomEvent('quiz:complete', {
          bubbles: false,
          detail: {
            quizData: quizInputs,
            quizUrl: baseUrl,
            recommender: this.quiz,
          },
        }),
      );

      this.quiz.redirectUrl = baseUrl.toString();
    }

    syncExclusiveStates() {
      this.querySelectorAll('input[type="checkbox"][data-exclusive]:checked').forEach((cb) => {
        const siblingInputs = [
          ...this.querySelectorAll(`input:not([type="checkbox"])[name="${cb.name}"]`),
          ...this.querySelectorAll(`select[name="${cb.name}"]`),
        ];
        siblingInputs.forEach((input) => {
          if (input.dataset.autocomplete !== undefined) {
            input.closest('input-group')?.changeInputValue('');
          } else {
            input.value = '';
          }
        });
      });
    }

    clearExclusiveCheckboxForInput(input) {
      const exclusiveCb = this.querySelector(`input[type="checkbox"][name="${input.name}"][data-exclusive]:checked`);
      if (!exclusiveCb) return;
      exclusiveCb.checked = false;
      this.saveCheckboxGroupToLocalStorage(input.name);
    }

    setupEventListeners() {
      if (this.backButton) {
        this.onBackButtonClick = () => {
          this.quiz.changeStep(
            this.quiz.savedPrevStepNames.length > 0 ? this.quiz.savedPrevStepNames.pop() : this.dataset.prevStepName,
            this,
            false,
          );
        };
        this.backButton.addEventListener('click', this.onBackButtonClick);
      }

      this.buttonClickHandlers = [];
      this.buttons.forEach((button) => {
        const handler = () => {
          const buttonNextStepNameOverride = button.dataset.nextStepName;
          // answered/localStorage must be recorded BEFORE changeStep, because changeStep calls
          // publishStepViewed for the next step — so answered(N) must fire before viewed(N+1).
          this.quiz.updateLocalStorageQuizInput(button.name, button.value);
          this.quiz.pushStepAnsweredEvent(this, { [button.name]: button.value });
          this.quiz.changeStep(
            buttonNextStepNameOverride ? buttonNextStepNameOverride : this.dataset.nextStepName,
            this,
          );
          this.quiz.updatePlaceholders(button.name, button.value);
        };
        this.buttonClickHandlers.push({ button, handler });
        button.addEventListener('click', handler);
      });

      this.inputs.forEach((input) => {
        if (input.pattern) {
          input.addEventListener('keypress', this.onInputKeyPress.bind(this));
        }
      });

      this.onChange = (event) => {
        const target = event.target;
        if (target.type === 'checkbox' && target.checked) {
          let unchecked = [];
          if (target.dataset.exclusive !== undefined) {
            unchecked = [
              ...this.querySelectorAll(`input[type="checkbox"][name="${target.name}"]:not([data-exclusive]):checked`),
            ];
            unchecked.forEach((cb) => {
              cb.checked = false;
            });

            // Clear sibling non-checkbox inputs with the same name
            const siblingInputs = [
              ...this.querySelectorAll(`input:not([type="checkbox"])[name="${target.name}"]`),
              ...this.querySelectorAll(`select[name="${target.name}"]`),
            ];
            siblingInputs.forEach((input) => {
              if (input.dataset.autocomplete !== undefined) {
                input.closest('input-group')?.changeInputValue('');
              } else {
                input.value = '';
              }
            });
            if (siblingInputs.length) {
              const quizData = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
              delete quizData[target.name];
              localStorage.setItem(this.quiz.storageKey, JSON.stringify(quizData));
            }
          } else {
            unchecked = [
              ...this.querySelectorAll(`input[type="checkbox"][name="${target.name}"][data-exclusive]:checked`),
            ];
            unchecked.forEach((cb) => {
              cb.checked = false;
            });
          }
          if (unchecked.length) {
            unchecked[0].dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        if (target.type !== 'checkbox' && target.dataset.quizInput !== undefined && target.value.trim() !== '') {
          this.clearExclusiveCheckboxForInput(target);
        }
        this.updateNextButtonState();
        if (target.type === 'checkbox' && target.dataset.quizInput !== undefined) {
          this.saveCheckboxGroupToLocalStorage(target.name);
          const savedCheckbox = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
          this.quiz.updatePlaceholders(target.name, savedCheckbox[target.name] || '');
        }
        if (target.type === 'radio' && target.dataset.quizInput !== undefined) {
          this.saveRadioToLocalStorage(target.name);
          const savedRadio = JSON.parse(localStorage.getItem(this.quiz.storageKey)) || {};
          this.quiz.updatePlaceholders(target.name, savedRadio[target.name] || '');
        }
      };
      this.addEventListener('change', this.onChange);

      this.onInput = (event) => {
        const target = event.target;
        if (target.type !== 'checkbox' && target.dataset.quizInput !== undefined && target.value.trim() !== '') {
          this.clearExclusiveCheckboxForInput(target);
        }
        this.updateNextButtonState();
      };
      this.addEventListener('input', this.onInput);
    }

    onInputKeyPress(event) {
      const input = event.target;
      // Skip per-keystroke blocking for email inputs or complex patterns (full-format validators).
      // Simple character-allowlist patterns like [A-Za-z0-9 ]* are safe to test per key;
      // full-format patterns (email regex, etc.) must only be evaluated on submit/blur.
      const isComplexPattern = input.type === 'email' || /[@{]/.test(input.pattern);
      if (isComplexPattern) return;

      if (!new RegExp(`^${input.pattern}$`).test(event.key)) {
        event.preventDefault();
      }
    }

    autocompleteValueValid(input) {
      const list = input.parentNode.querySelector('[data-autocomplete-list]');
      if (!list) return false;
      const buttonValues = Array.from(list.querySelectorAll('button'), (button) => button.value.toLowerCase());
      return buttonValues.includes(input.value.toLowerCase());
    }
  }

  customElements.define('quiz-step', QuizStep);
}

if (!customElements.get('quiz-animated-image')) {
  class QuizAnimatedImage extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this.dataset.location === 'progress_bar') {
        const step = this.closest('quiz-step');
        if (!step) return;
        const bar = step.querySelector('.quiz__step-progress');
        if (!bar) return;
        if (bar.contains(this)) return;
        bar.appendChild(this);
      }
    }

    disconnectedCallback() {}
  }

  customElements.define('quiz-animated-image', QuizAnimatedImage);
}

if (!customElements.get('quiz-loader')) {
  class QuizLoader extends HTMLElement {
    connectedCallback() {
      this.fillEl = this.querySelector('.quiz__loader__fill');
      this.itemEls = this.querySelectorAll('.quiz__loader__item');
      this.duration = parseInt(this.dataset.duration, 10) || 3;

      if (!this.fillEl) return;

      this.boundOnStepActivated = this.onStepActivated.bind(this);
      this.boundOnAnimationEnd = this.onFillAnimationEnd.bind(this);

      const step = this.closest('quiz-step');
      if (step) {
        step.addEventListener('quiz:stepActivated', this.boundOnStepActivated);
      }

      this.fillEl.addEventListener('animationend', this.boundOnAnimationEnd);
    }

    disconnectedCallback() {
      clearInterval(this.itemInterval);
      clearTimeout(this.fallbackTimeout);
      const step = this.closest('quiz-step');
      if (step) step.removeEventListener('quiz:stepActivated', this.boundOnStepActivated);
      this.fillEl?.removeEventListener('animationend', this.boundOnAnimationEnd);
    }

    onStepActivated() {
      if (Shopify.designMode) return;

      if (this.itemEls.length > 0) {
        this.itemEls[0].classList.add('quiz__loader__item--active');
      }

      const stepMs = (this.duration * 1000) / Math.max(this.itemEls.length, 1);
      let currentIndex = 0;
      // intentional setInterval: cycles loading item text evenly across animation duration
      this.itemInterval = setInterval(() => {
        this.itemEls[currentIndex]?.classList.remove('quiz__loader__item--active');
        currentIndex = Math.min(currentIndex + 1, this.itemEls.length - 1);
        this.itemEls[currentIndex]?.classList.add('quiz__loader__item--active');
        if (currentIndex >= this.itemEls.length - 1) {
          clearInterval(this.itemInterval);
        }
      }, stepMs);

      this.animationFired = false;
      this.classList.add('quiz__loader--animating');
      // intentional setTimeout: safety net if animationend is suppressed (e.g. prefers-reduced-motion)
      this.fallbackTimeout = setTimeout(
        () => {
          if (!this.animationFired) this.onFillAnimationEnd();
        },
        (this.duration + 0.5) * 1000,
      );
    }

    onFillAnimationEnd() {
      this.animationFired = true;
      clearTimeout(this.fallbackTimeout);
      clearInterval(this.itemInterval);
      if (this.itemEls.length > 0) {
        this.itemEls.forEach((el) => el.classList.remove('quiz__loader__item--active'));
        this.itemEls[this.itemEls.length - 1].classList.add('quiz__loader__item--active');
      }
      const step = this.closest('quiz-step');
      if (!step) return;
      const nextStepName = step.dataset.nextStepName;
      if (!nextStepName) return;
      step.quiz.changeStep(nextStepName, step, false);
    }
  }

  customElements.define('quiz-loader', QuizLoader);
}
